import React, { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { MaterialIcons } from "@expo/vector-icons";

import Colors from "../../../../Resources/Color";
import {
  InlineNutritionSummary,
  QuantityControl,
  useResponsiveIngredientRowLayout,
} from "./FoodRowShared";
import NutritionConflictWarningIcon from "../NutritionConflictWarningIcon";
import { useDevice } from "../../../_layout";

function getSelectedFoodRowDragHandleElementProps(dragHandleProps) {
  if (!dragHandleProps) {
    return {};
  }

  if (dragHandleProps.dragHandleProps) {
    return dragHandleProps.dragHandleProps;
  }

  return {
    ...(dragHandleProps.attributes ?? {}),
    ...(dragHandleProps.listeners ?? {}),
  };
}

export function SelectedFoodRow({
  title,
  subtitle,
  quantityValue,
  onChangeQuantity,
  unitOptions,
  selectedUnitKey,
  onSelectUnit,
  referenceText,
  totals,
  compactLayout = false,
  actionSlot = null,
  footer = null,
  leadingSlot = null,
  dragHandleProps = null,
  isLast = false,
  children = null,
  showQuantityControl = true,
  showNutritionSummary = true,
  showNutritionSummaryCalories = true,
  nutritionSummaryLabel = null,
  warningTooltip = "",
  isAutoCalculated = false,
  autoCalculatedTooltip = "",
  showDivider = true,
  trimBottomPadding = false,
  isReadOnlyPreview = false,
  chromeVariant = "default",
  containerStyle = null,
}) {
  const [isDragHandleHovered, setIsDragHandleHovered] = useState(false);
  const { isMobile } = useDevice();
  const { handleRowLayout, shouldUseCompactLayout } =
    useResponsiveIngredientRowLayout(compactLayout);
  const dragHandleElementProps =
    getSelectedFoodRowDragHandleElementProps(dragHandleProps);

  return (
    <View
      style={[
        styles.selectedIngredientCard,
        !showDivider ? styles.selectedIngredientCardNoDivider : null,
        trimBottomPadding ? styles.selectedIngredientCardNoBottomPadding : null,
        isLast ? styles.selectedIngredientCardLast : null,
        containerStyle,
      ]}
    >
      <View
        onLayout={handleRowLayout}
        style={[
          styles.selectedIngredientShellRow,
          shouldUseCompactLayout ? styles.selectedIngredientShellRowCompact : null,
        ]}
      >
        {dragHandleProps ? (
          <View style={styles.selectedIngredientHandleRail}>
            <div
              style={styles.dragHandleContainer}
              onMouseEnter={() => !isMobile && setIsDragHandleHovered(true)}
              onMouseLeave={() => setIsDragHandleHovered(false)}
              {...dragHandleElementProps}
            >
              <MaterialIcons
                name="drag-indicator"
                size={16}
                color={
                  dragHandleProps.isDragging || isDragHandleHovered
                    ? Colors.darkgrayColor
                    : Colors.grayColor
                }
                style={styles.dragHandleIcon}
              />
            </div>
          </View>
        ) : null}

        {leadingSlot ? <View style={styles.leadingRail}>{leadingSlot}</View> : null}

        <View style={styles.selectedIngredientContentColumn}>
          <View
            style={[
              styles.selectedIngredientMainRow,
              shouldUseCompactLayout ? styles.selectedIngredientMainRowCompact : null,
            ]}
          >
            <View
              style={[
                styles.selectedIngredientIdentity,
                shouldUseCompactLayout
                  ? styles.selectedIngredientIdentityCompact
                  : styles.selectedIngredientIdentityDesktop,
              ]}
            >
              {showQuantityControl ? (
                <QuantityControl
                  value={quantityValue}
                  onChangeText={onChangeQuantity}
                  unitOptions={unitOptions}
                  selectedUnitKey={selectedUnitKey}
                  onSelectUnit={onSelectUnit}
                  referenceText={referenceText}
                  isAutoCalculated={isAutoCalculated}
                  autoCalculatedTooltip={autoCalculatedTooltip}
                  isReadOnly={isReadOnlyPreview}
                  chromeVariant={chromeVariant}
                />
              ) : null}
              <View style={styles.ingredientInfo}>
                <View style={styles.ingredientNameRow}>
                  <Text numberOfLines={1} style={styles.ingredientName}>
                    {title}
                  </Text>
                  <NutritionConflictWarningIcon
                    tooltip={warningTooltip}
                    size={13}
                    style={styles.warningIcon}
                  />
                </View>
                <Text numberOfLines={1} style={styles.ingredientSource}>
                  {subtitle}
                </Text>
              </View>
            </View>

            <View
              style={[
                styles.selectedIngredientUtilityRow,
                shouldUseCompactLayout
                  ? styles.selectedIngredientUtilityRowCompact
                  : null,
              ]}
            >
              {showNutritionSummary ? (
                <View
                  style={[
                    styles.selectedIngredientNutrition,
                    shouldUseCompactLayout
                      ? styles.selectedIngredientNutritionCompact
                      : null,
                  ]}
                >
                  {nutritionSummaryLabel ? (
                    <Text style={styles.nutritionSummaryLabel}>
                      {nutritionSummaryLabel}
                    </Text>
                  ) : null}
                  <InlineNutritionSummary
                    calories={totals?.calories}
                    fats={totals?.fats}
                    carbs={totals?.carbs}
                    protein={totals?.protein}
                    showCalories={showNutritionSummaryCalories}
                  />
                </View>
              ) : null}
              {actionSlot}
            </View>
          </View>
          {children}
        </View>
      </View>
      {footer}
    </View>
  );
}

const styles = StyleSheet.create({
  selectedIngredientCard: {
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: "rgba(63, 123, 141, 0.12)",
  },
  selectedIngredientCardNoDivider: {
    borderBottomWidth: 0,
  },
  selectedIngredientCardNoBottomPadding: {
    paddingBottom: 0,
  },
  selectedIngredientCardLast: {
    borderBottomWidth: 0,
  },
  dragHandleContainer: {
    padding: 4,
    cursor: "grab",
    touchAction: "none",
  },
  selectedIngredientHandleRail: {
    width: 24,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  dragHandleIcon: {
    transition: "color 200ms ease-in-out",
  },
  leadingRail: {
    width: 28,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  selectedIngredientShellRow: {
    flexDirection: "row",
    alignItems: "stretch",
    gap: 10,
    minWidth: 0,
  },
  selectedIngredientShellRowCompact: {
    alignItems: "stretch",
  },
  selectedIngredientContentColumn: {
    flex: 1,
    minWidth: 0,
  },
  selectedIngredientMainRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexWrap: "nowrap",
  },
  selectedIngredientMainRowCompact: {
    alignItems: "flex-start",
    flexWrap: "wrap",
    rowGap: 4,
  },
  selectedIngredientIdentity: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 320,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  selectedIngredientIdentityDesktop: {
    flexWrap: "nowrap",
  },
  selectedIngredientIdentityCompact: {
    flex: 1,
    width: "100%",
    flexWrap: "wrap",
    alignItems: "flex-start",
    rowGap: 4,
  },
  ingredientInfo: {
    flex: 1,
    minWidth: 0,
  },
  ingredientNameRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: 6,
    minWidth: 0,
  },
  ingredientName: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "500",
    color: Colors.blackColor,
    flexShrink: 1,
  },
  ingredientSource: {
    marginTop: 3,
    fontSize: 10,
    lineHeight: 12,
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.45,
  },
  warningIcon: {
    flexShrink: 0,
  },
  selectedIngredientUtilityRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    marginLeft: "auto",
    maxWidth: "100%",
    minWidth: 0,
    flexShrink: 1,
  },
  selectedIngredientUtilityRowCompact: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 0,
  },
  selectedIngredientNutrition: {
    minWidth: 0,
    flexShrink: 1,
    alignItems: "flex-end",
  },
  selectedIngredientNutritionCompact: {
    flex: 1,
    minWidth: 0,
    alignItems: "flex-start",
  },
  nutritionSummaryLabel: {
    fontSize: 10,
    lineHeight: 13,
    fontWeight: "400",
    color: Colors.grayColor,
    marginBottom: 4,
  },
});
